import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import useSEO from "../lib/useSEO";
import { Save, Loader2, Search, Settings2, KeyRound } from "lucide-react";
import ChangePasswordModal from "../components/ChangePasswordModal";
import AdminAnalyticsTab from "../components/AdminAnalyticsTab";
import AdminResearchQualityTab from "../components/AdminResearchQualityTab";
import AdminLeadsTab from "../components/AdminLeadsTab";
import AdminTrafficTab from "../components/AdminTrafficTab";
import { AdminAffiliatesTab, AdminCommissionsTab } from "../components/AdminAffiliateTabs";
import AdminReviewsTab from "../components/AdminReviewsTab";
import AdminUsersTab from "../components/AdminUsersTab";
import { PAYMENT_STATUS_META, PLAN_META } from "../components/PaymentStatusBadge";

const BANDS = ["A", "B", "C", "D", "E"];

export default function AdminDashboardPage() {
    const { user, loading: authLoading } = useAuth();
    const [stats, setStats] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    // Single source of truth for the active tab is the URL — eliminates the
    // race where setTab + setSearchParams in the same event handler could
    // overwrite each other and strip the `role` query param.
    const tab = searchParams.get("tab") || "analytics";
    const setTab = (t) => {
        const np = new URLSearchParams(searchParams);
        np.set("tab", t);
        setSearchParams(np, { replace: true });
    };
    const [pricing, setPricing] = useState([]);
    const [categories, setCategories] = useState([]);
    const [states, setStates] = useState([]);
    const [users, setUsers] = useState([]);
    const [activeCat, setActiveCat] = useState("");
    const [savingId, setSavingId] = useState(null);
    const [savedId, setSavedId] = useState(null);

    useSEO({ title: "Admin Dashboard", path: "/admin/dashboard", noindex: true });

    // County tab state
    const [countyState, setCountyState] = useState("");
    const [countyList, setCountyList] = useState([]);
    const [countySearch, setCountySearch] = useState("");
    const [editingCounty, setEditingCounty] = useState(null); // full panel

    // Inventory tab state
    const [invState, setInvState] = useState("");
    const [invRows, setInvRows] = useState([]);
    const [showPwModal, setShowPwModal] = useState(false);

    useEffect(() => {
        if (!user || user.role !== "admin") return;
        Promise.all([
            api.get("/admin/stats"),
            api.get("/admin/pricing"),
            api.get("/categories"),
            api.get("/states"),
            api.get("/admin/users"),
        ]).then(([s, p, c, st, us]) => {
            setStats(s.data);
            setPricing(p.data);
            setCategories(c.data);
            setStates(st.data);
            setUsers(us.data);
            if (c.data.length) setActiveCat(c.data[0].id);
        }).catch(() => {});
    }, [user]);

    // Lazy-load counties when state chosen
    useEffect(() => {
        if (!countyState) { setCountyList([]); return; }
        api.get("/counties", { params: { state_code: countyState } }).then(({ data }) => {
            setCountyList(data);
        });
    }, [countyState]);

    useEffect(() => {
        if (!invState) { setInvRows([]); return; }
        api.get("/admin/inventory", { params: { state_code: invState, limit: 200 } }).then(({ data }) => {
            setInvRows(data);
        });
    }, [invState]);

    const updatePrice = async (row) => {
        setSavingId(row.id);
        try {
            await api.put(`/admin/pricing/${row.id}`, {
                basic_price: row.basic_price,
                featured_price: row.featured_price,
                top_spot_price: row.top_spot_price,
            });
            setSavedId(row.id);
            setTimeout(() => setSavedId(null), 1200);
        } finally { setSavingId(null); }
    };

    const updateBand = async (cnty, band) => {
        await api.put(`/admin/county/${cnty.id}/band`, { demand_band: band });
        setCountyList(cs => cs.map(c => c.id === cnty.id ? { ...c, demand_band: band } : c));
    };

    const filteredPricing = pricing.filter(p => p.category_id === activeCat).sort((a, b) => a.band.localeCompare(b.band));

    const visibleCounties = useMemo(() => {
        const q = countySearch.trim().toLowerCase();
        const list = q ? countyList.filter(c => c.name.toLowerCase().includes(q)) : countyList;
        return list.slice(0, 100);
    }, [countyList, countySearch]);

    if (authLoading) {
        return (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-sm text-muted-foreground" data-testid="admin-dashboard-loading">Loading…</div>
            </section>
        );
    }
    if (!user) return <Navigate to="/admin/auth" replace />;
    if (user.role !== "admin") return <Navigate to="/" replace />;

    return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="ll-label mb-2">Admin Console</div>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
                <div>
                    <h1 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight" data-testid="admin-dashboard-title">
                        Inventory & Pricing Management
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Welcome back, {user.name}.</p>
                </div>
                <button onClick={() => setShowPwModal(true)}
                        className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2 text-xs font-semibold hover:bg-accent"
                        data-testid="open-admin-pw-btn">
                    <KeyRound className="h-3.5 w-3.5" /> Change password
                </button>
            </div>
            <div className="mb-8" />

            <ChangePasswordModal open={showPwModal} onClose={() => setShowPwModal(false)} />

            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="admin-stats">
                    {[
                        { label: "Attorneys", value: stats.attorneys, role: "attorney" },
                        { label: "Consumers", value: stats.consumers, role: "consumer" },
                        { label: "Affiliates", value: stats.affiliates, role: "affiliate" },
                        { label: "Admins", value: stats.admins, role: "admin" },
                        { label: "Active Placements", value: stats.active_placements, role: null },
                    ].map(({ label, value, role }) => {
                        const clickable = role !== null;
                        const Wrapper = clickable ? "button" : "div";
                        return (
                            <Wrapper
                                key={label}
                                {...(clickable ? {
                                    onClick: () => {
                                        const np = new URLSearchParams(searchParams);
                                        np.set("tab", "users");
                                        np.set("role", role);
                                        setSearchParams(np, { replace: true });
                                    },
                                    type: "button",
                                    "data-testid": `stats-card-${role}`,
                                } : {})}
                                className={`ll-card text-left ${clickable ? "ll-card-hover cursor-pointer" : ""}`}>
                                <div className="ll-label mb-1">{label}</div>
                                <div className="font-serif text-3xl font-bold">{value ?? 0}</div>
                                {clickable && (
                                    <div className="mt-1 text-[11px] text-primary font-medium">Manage →</div>
                                )}
                            </Wrapper>
                        );
                    })}
                </div>
            )}

            {/* Attorney payment health — instant read on who is paying. */}
            {stats?.attorney_payments && (
                <section className="mb-10" data-testid="admin-payment-health">
                    <div className="flex items-baseline justify-between gap-3 mb-2.5 flex-wrap">
                        <h2 className="font-serif text-xl font-semibold">Attorney payment health</h2>
                        <p className="text-xs text-muted-foreground">
                            {stats.attorney_payments.paying_count} paying · {stats.attorney_payments.non_paying_count} not paying · {stats.attorney_payments.total_attorneys_active_accounts} total active accounts
                        </p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="payment-status-cards">
                        {[
                            { key: "active", label: "Active / Paid" },
                            { key: "pending", label: "Pending" },
                            { key: "past_due", label: "Past Due" },
                            { key: "cancelled", label: "Cancelled" },
                            { key: "expired", label: "Expired" },
                            { key: "unpaid", label: "Unpaid" },
                        ].map(({ key, label }) => {
                            const meta = PAYMENT_STATUS_META[key];
                            const count = stats.attorney_payments.by_status?.[key] ?? 0;
                            return (
                                <button key={key}
                                        type="button"
                                        onClick={() => {
                                            const np = new URLSearchParams(searchParams);
                                            np.set("tab", "users");
                                            np.set("role", "attorney");
                                            np.set("payment_status", key);
                                            setSearchParams(np, { replace: true });
                                        }}
                                        data-testid={`payment-card-${key}`}
                                        className="ll-card ll-card-hover text-left cursor-pointer">
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                        <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                                        <span className="ll-label">{label}</span>
                                    </div>
                                    <div className="font-serif text-2xl font-bold">{count}</div>
                                    <div className="mt-1 text-[11px] text-primary font-medium">View attorneys →</div>
                                </button>
                            );
                        })}
                    </div>
                    {/* Plan mix among currently active subscribers */}
                    <div className="mt-4 ll-card" data-testid="payment-plan-mix">
                        <div className="flex items-baseline justify-between mb-2 gap-2 flex-wrap">
                            <h3 className="ll-label">Plan mix — currently active</h3>
                            <span className="text-[11px] text-muted-foreground">Top tier per attorney across all counties.</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            {[
                                { key: "top_spot", label: PLAN_META.top_spot.label },
                                { key: "featured", label: PLAN_META.featured.label },
                                { key: "basic", label: PLAN_META.basic.label },
                            ].map(({ key, label }) => (
                                <div key={key} className={`rounded-xl border ${PLAN_META[key].className} px-3 py-2.5 text-center`} data-testid={`plan-mix-${key}`}>
                                    <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
                                    <div className="font-serif text-xl font-bold mt-0.5">{stats.attorney_payments.by_plan?.[key] ?? 0}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <div className="flex flex-wrap gap-2 mb-6 border-b border-border" data-testid="admin-tabs">
                {[
                    ["analytics", "Analytics"],
                    ["traffic", "Traffic"],
                    ["research-quality", "Legal AI Quality"],
                    ["leads", "Leads"],
                    ["reviews", "Reviews"],
                    ["affiliates", "Affiliates"],
                    ["commissions", "Commissions"],
                    ["pricing", "Base Pricing"],
                    ["counties", "Counties & Overrides"],
                    ["inventory", "Elite Inventory"],
                    ["users", "Users"],
                ].map(([k, label]) => (
                    <button key={k} onClick={() => setTab(k)}
                            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                                tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid={`admin-tab-${k}`}>
                        {label}
                    </button>
                ))}
            </div>

            {tab === "analytics" && <AdminAnalyticsTab />}
            {tab === "traffic" && <AdminTrafficTab />}
            {tab === "research-quality" && <AdminResearchQualityTab />}
            {tab === "leads" && <AdminLeadsTab />}
            {tab === "reviews" && <AdminReviewsTab />}
            {tab === "affiliates" && <AdminAffiliatesTab />}
            {tab === "commissions" && <AdminCommissionsTab />}

            {tab === "pricing" && (
                <div>
                    <p className="text-sm text-muted-foreground mb-4">
                        Base price book — applied to every county in the matching demand band, then overridable per county.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-6">
                        {categories.map((c) => (
                            <button key={c.id} onClick={() => setActiveCat(c.id)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                                        activeCat === c.id ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:bg-accent"
                                    }`}>
                                {c.name}
                            </button>
                        ))}
                    </div>
                    <div className="ll-card overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left">
                                    <th className="ll-label py-3 pr-4">Band</th>
                                    <th className="ll-label py-3 pr-4">Basic</th>
                                    <th className="ll-label py-3 pr-4">Featured</th>
                                    <th className="ll-label py-3 pr-4">Elite</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPricing.map((row) => (
                                    <tr key={row.id} className="border-t border-border">
                                        <td className="py-3 pr-4 font-serif font-semibold text-lg">Band {row.band}</td>
                                        {["basic_price", "featured_price", "top_spot_price"].map((k) => (
                                            <td key={k} className="py-3 pr-4">
                                                <input
                                                    type="number"
                                                    className="ll-input max-w-[120px]"
                                                    value={row[k]}
                                                    onChange={(e) => {
                                                        const val = parseFloat(e.target.value || 0);
                                                        setPricing(ps => ps.map(p => p.id === row.id ? { ...p, [k]: val } : p));
                                                    }}
                                                    data-testid={`admin-price-input-${row.id}-${k}`}
                                                />
                                            </td>
                                        ))}
                                        <td className="py-3">
                                            <button onClick={() => updatePrice(row)}
                                                    className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold hover:bg-primary/90"
                                                    data-testid={`save-price-${row.id}`}>
                                                {savingId === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                                {savedId === row.id ? "Saved!" : "Save"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === "counties" && (
                <div>
                    <div className="flex flex-wrap gap-3 mb-5 items-end">
                        <div>
                            <label className="ll-label block mb-1.5">State</label>
                            <select className="ll-input min-w-[200px]" value={countyState}
                                    onChange={e => setCountyState(e.target.value)}
                                    data-testid="admin-county-state-select">
                                <option value="">Choose a state…</option>
                                {states.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                            </select>
                        </div>
                        {countyList.length > 0 && (
                            <div className="flex-1 min-w-[200px]">
                                <label className="ll-label block mb-1.5">Search counties ({countyList.length})</label>
                                <div className="relative">
                                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <input className="ll-input pl-9" value={countySearch}
                                           onChange={e => setCountySearch(e.target.value)}
                                           placeholder="e.g. Fulton" data-testid="admin-county-search" />
                                </div>
                            </div>
                        )}
                    </div>

                    {!countyState ? (
                        <div className="ll-card text-sm text-muted-foreground" data-testid="admin-counties-empty">
                            Pick a state above to view all counties, change demand bands, or set per-county pricing overrides. {stats?.counties ?? 0} counties available across all 50 states + DC.
                        </div>
                    ) : (
                        <div className="ll-card overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left">
                                        <th className="ll-label py-3 pr-4">County</th>
                                        <th className="ll-label py-3 pr-4 text-right">Population</th>
                                        <th className="ll-label py-3 pr-4">Band</th>
                                        <th className="ll-label py-3 pr-4">Elite Cap</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleCounties.map(c => (
                                        <tr key={c.id} className="border-t border-border" data-testid={`admin-county-row-${c.id}`}>
                                            <td className="py-3 pr-4 font-medium">{c.name}</td>
                                            <td className="py-3 pr-4 text-right tabular-nums text-muted-foreground">{c.population?.toLocaleString() || "—"}</td>
                                            <td className="py-3 pr-4">
                                                <div className="flex gap-1">
                                                    {BANDS.map(b => (
                                                        <button key={b}
                                                                onClick={() => updateBand(c, b)}
                                                                className={`h-7 w-7 rounded-md text-xs font-semibold transition ${
                                                                    c.demand_band === b ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:bg-accent"
                                                                }`}
                                                                data-testid={`admin-band-${c.id}-${b}`}>{b}</button>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="py-3 pr-4 text-xs text-muted-foreground">
                                                {c.top_spot_cap_override ? <span className="font-semibold text-foreground">{c.top_spot_cap_override} (custom)</span> : "Default"}
                                            </td>
                                            <td className="py-3">
                                                <button onClick={() => setEditingCounty(c)}
                                                        className="inline-flex items-center gap-1.5 rounded-full bg-card border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent"
                                                        data-testid={`admin-edit-county-${c.id}`}>
                                                    <Settings2 className="h-3 w-3" /> Pricing
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {countyList.length > 100 && countySearch === "" && (
                                <div className="text-xs text-muted-foreground mt-3">Showing first 100 — type to search the remaining {countyList.length - 100}.</div>
                            )}
                        </div>
                    )}

                    {editingCounty && (
                        <CountyOverridePanel
                            county={editingCounty}
                            categories={categories}
                            onClose={() => setEditingCounty(null)}
                            onCountyChange={(c) => setCountyList(cs => cs.map(x => x.id === c.id ? c : x))}
                        />
                    )}
                </div>
            )}

            {tab === "inventory" && (
                <div>
                    <div className="flex flex-wrap gap-3 mb-5 items-end">
                        <div>
                            <label className="ll-label block mb-1.5">State</label>
                            <select className="ll-input min-w-[200px]" value={invState}
                                    onChange={e => setInvState(e.target.value)}
                                    data-testid="admin-inv-state-select">
                                <option value="">Choose a state…</option>
                                {states.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                            </select>
                        </div>
                    </div>

                    {!invState ? (
                        <div className="ll-card text-sm text-muted-foreground">Pick a state to view Elite inventory across counties × categories.</div>
                    ) : (
                        <div className="ll-card overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left">
                                        <th className="ll-label py-3 pr-4">County</th>
                                        <th className="ll-label py-3 pr-4">Category</th>
                                        <th className="ll-label py-3 pr-4">Band</th>
                                        <th className="ll-label py-3 pr-4 text-right">Used / Cap</th>
                                        <th className="ll-label py-3 pr-4">Status</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="inventory-rows">
                                    {invRows.slice(0, 200).map((row, i) => (
                                        <tr key={i} className="border-t border-border">
                                            <td className="py-2.5 pr-4">{row.county_name}</td>
                                            <td className="py-2.5 pr-4">{row.category_name}</td>
                                            <td className="py-2.5 pr-4">{row.band}</td>
                                            <td className="py-2.5 pr-4 text-right tabular-nums">{row.top_spot_used} / {row.top_spot_capacity}</td>
                                            <td className="py-2.5 pr-4">
                                                <span className={`ll-pill ${row.is_full ? "bg-destructive/15 text-destructive" : ""}`}>
                                                    {row.is_full ? "Full" : "Available"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {invRows.length === 0 && <div className="text-xs text-muted-foreground p-4">Loading or no inventory rows.</div>}
                        </div>
                    )}
                </div>
            )}

            {tab === "users" && (
                <AdminUsersTab
                    initialRole={searchParams.get("role") || "all"}
                    initialPaymentStatus={searchParams.get("payment_status") || "all"}
                    onChanged={() => { api.get("/admin/users").then(({ data }) => setUsers(data)); api.get("/admin/stats").then(({ data }) => setStats(data)); }}
                />
            )}
        </section>
    );
}

/* --- County override panel (modal) --- */
function CountyOverridePanel({ county, categories, onClose, onCountyChange }) {
    const [overrides, setOverrides] = useState([]);
    const [activeCat, setActiveCat] = useState(categories[0]?.id || "");
    const [form, setForm] = useState({ basic_price: "", featured_price: "", top_spot_price: "" });
    const [topSpotCap, setTopSpotCap] = useState(county.top_spot_cap_override ?? "");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => {
        api.get(`/admin/county/${county.id}/overrides`).then(({ data }) => setOverrides(data.overrides || []));
    }, [county.id]);

    useEffect(() => {
        const ex = overrides.find(o => o.category_id === activeCat);
        setForm({
            basic_price: ex?.basic_price ?? "",
            featured_price: ex?.featured_price ?? "",
            top_spot_price: ex?.top_spot_price ?? "",
        });
    }, [activeCat, overrides]);

    const save = async () => {
        setSaving(true); setMsg("");
        try {
            const payload = {};
            for (const k of ["basic_price", "featured_price", "top_spot_price"]) {
                if (form[k] !== "" && form[k] !== null) payload[k] = parseFloat(form[k]);
            }
            await api.put(`/admin/county/${county.id}/category/${activeCat}/override`, payload);
            const { data } = await api.get(`/admin/county/${county.id}/overrides`);
            setOverrides(data.overrides || []);
            setMsg("✓ Saved");
            setTimeout(() => setMsg(""), 1500);
        } catch (e) {
            setMsg("Save failed");
        } finally { setSaving(false); }
    };

    const clearOverride = async () => {
        await api.delete(`/admin/county/${county.id}/category/${activeCat}/override`);
        const { data } = await api.get(`/admin/county/${county.id}/overrides`);
        setOverrides(data.overrides || []);
        setForm({ basic_price: "", featured_price: "", top_spot_price: "" });
    };

    const saveCap = async () => {
        const cap = topSpotCap === "" ? null : parseInt(topSpotCap, 10);
        const { data } = await api.put(`/admin/county/${county.id}/top-spot-cap`, { top_spot_cap: cap });
        onCountyChange(data);
    };

    return (
        <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-card rounded-lg shadow-xl max-w-xl w-full p-6 sm:p-8" onClick={e => e.stopPropagation()} data-testid="county-override-modal">
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <div className="ll-label mb-1">County Override</div>
                        <h3 className="font-serif text-2xl font-bold">{county.name}, {county.state_code}</h3>
                        <div className="text-xs text-muted-foreground mt-1">
                            Population {county.population?.toLocaleString() || "—"} · Band {county.demand_band}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">✕</button>
                </div>

                {/* Top spot cap */}
                <div className="mb-5 p-4 rounded-md bg-accent/30 border border-border">
                    <label className="ll-label block mb-1.5">Elite capacity override</label>
                    <div className="flex gap-2 items-center">
                        <input className="ll-input max-w-[120px]" type="number" placeholder="default"
                               value={topSpotCap} onChange={e => setTopSpotCap(e.target.value)}
                               data-testid="county-top-spot-cap-input" />
                        <button onClick={saveCap}
                                className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold hover:bg-primary/90"
                                data-testid="save-top-spot-cap-btn">
                            <Save className="h-3 w-3" /> Save cap
                        </button>
                        <span className="text-xs text-muted-foreground">Leave blank to use band default.</span>
                    </div>
                </div>

                {/* Category override editor */}
                <label className="ll-label block mb-1.5">Practice area</label>
                <select className="ll-input mb-4" value={activeCat} onChange={e => setActiveCat(e.target.value)}
                        data-testid="override-cat-select">
                    {categories.map(c => (
                        <option key={c.id} value={c.id}>
                            {c.name}{overrides.some(o => o.category_id === c.id) ? " · custom" : ""}
                        </option>
                    ))}
                </select>

                <div className="grid grid-cols-3 gap-3">
                    {["basic_price", "featured_price", "top_spot_price"].map(k => (
                        <div key={k}>
                            <label className="ll-label block mb-1.5">{k.replace("_price", "").replace("_", " ")}</label>
                            <input className="ll-input" type="number" placeholder="default"
                                   value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
                                   data-testid={`override-${k}-input`} />
                        </div>
                    ))}
                </div>

                <div className="mt-5 flex items-center gap-3">
                    <button onClick={save} disabled={saving}
                            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
                            data-testid="save-override-btn">
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                        Save override
                    </button>
                    <button onClick={clearOverride}
                            className="text-sm text-muted-foreground hover:text-destructive"
                            data-testid="clear-override-btn">
                        Clear override
                    </button>
                    {msg && <span className="text-xs text-primary">{msg}</span>}
                </div>

                <div className="mt-5 text-[11px] text-muted-foreground">
                    Leave a tier blank to fall back to the base band price. Global pricing caps still apply.
                </div>
            </div>
        </div>
    );
}
